/**
 * Tracking utility functions for Netlizing frontend
 *
 * This file contains low-level utility functions for tracking.
 * These functions are used by the tracking service but don't make API calls themselves.
 */

// Store for custom parameters to be included in tracking payloads
const customParams = {};

/**
 * Get a cookie value by name
 *
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string} The cookie value or empty string if not found
 */
export function getCookie(name) {
    const match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? decodeURIComponent(match[2]) : "";
}

/**
 * Generate a client ID according to the specification:
 * Format: 'generated_' + [random number, timestamp]
 *
 * @returns {string} A unique client ID
 */
export function generateClientId() {
    const timestamp = Math.round(+new Date() / 1000.0);
    let randomValue;

    try {
        const uint32Array = new Uint32Array(1);
        randomValue = crypto.getRandomValues(uint32Array)[0];
    } catch (e) {
        randomValue = Math.round(Math.random() * 2147483647);
    }

    return "generated_" + [randomValue, timestamp].join(".");
}

/**
 * Parse URL parameters from a URL string
 *
 * @param {string} url - The URL to parse parameters from
 * @returns {Object} An object containing all URL parameters
 */
export function getUrlParameters(url = window.location.href) {
    const params = {};
    const parser = document.createElement("a");
    parser.href = url;

    const query = parser.search.substring(1);
    const vars = query.split("&");

    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split("=");
        if (pair[0] !== "") {
            params[pair[0]] = decodeURIComponent(pair[1] || "");
        }
    }

    return params;
}

/**
 * Get the current page/section from URL or fallback to document title
 *
 * @returns {string} The current page or section name
 */
export function getCurrentPage() {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const lastPathPart = pathParts[pathParts.length - 1] || "home";

    return lastPathPart.replace(/[-_]/g, " ");
}

/**
 * Collect basic tracking data from cookies and browser
 *
 * @returns {Object} Object containing _gclid, _fbp, _fbc, and user_agent
 */
export function collectTrackingData() {
    // Get tracking cookies
    const _gclid = getCookie("_gclid") || getUrlParameters().gclid || "";
    const _fbp = getCookie("_fbp") || "";
    const _fbc = getCookie("_fbc") || "";

    // Get user agent
    const user_agent = navigator.userAgent;

    return {
        _gclid,
        _fbp,
        _fbc,
        user_agent
    };
}

/**
 * Store a custom parameter for later inclusion in tracking payloads
 *
 * @param {string|Object} param - The parameter to store
 */
export function passParam(param) {
    if (typeof param === "string") {
        // Split the parameter into key-value parts if it contains '='
        if (param.includes("=")) {
            const [key, value] = param.split("=", 2);
            const trimmedKey = key.trim();
            // Don't trim gtm_preview or gtm_debug values as they are base64 encoded
            if (trimmedKey === 'gtm_preview' || trimmedKey === 'gtm_debug') {
                customParams[trimmedKey] = value;
            } else {
                customParams[trimmedKey] = value.trim();
            }
        } else {
            // If it's just a value without key, store as gtm_preview (don't trim base64)
            customParams.gtm_preview = param;
        }
    } else if (typeof param === "object" && param !== null) {
        // If it's an object, merge it with existing params
        Object.assign(customParams, param);
    }
}

/**
 * Get all custom parameters stored with passParam
 *
 * @returns {Object} Object containing all stored custom parameters
 */
export function getCustomParams() {
    return { ...customParams };
}

/**
 * Store a value in localStorage with a prefix
 *
 * @param {string} key - Key to store under (without prefix)
 * @param {string} value - Value to store
 * @param {string} prefix - Prefix to use (default: 'nl_')
 */
export function storeLocalValue(key, value, prefix = "nl_") {
    if (key && value !== undefined) {
        localStorage.setItem(prefix + key, value);
    }
}

/**
 * Get a value from localStorage with a prefix
 *
 * @param {string} key - Key to retrieve (without prefix)
 * @param {string} prefix - Prefix to use (default: 'nl_')
 * @returns {string|null} The stored value or null if not found
 */
export function getLocalValue(key, prefix = "nl_") {
    return localStorage.getItem(prefix + key);
}

/**
 * Extract numeric value from a formatted string
 * For example: "503 987 Ft + ÁFA / hó" -> "503987"
 *
 * @param {string} text - The text to extract from
 * @returns {string} The extracted numeric value or "0" if not found
 */
export function extractNumericValue(text) {
    if (!text) return "0";

    const match = text.match(/(\d[\d\s]+)/);
    if (match && match[1]) {
        return match[1].replace(/\s/g, "");
    }

    return "0";
}

/**
 * Get the cookie consent level from localStorage
 *
 * @returns {Object} Object with consent status for different categories
 */
export function getConsentLevel() {
    const savedConsent = localStorage.getItem("consentMode");
    const defaultConsent = {
        ad_storage: "denied",
        analytics_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted"
    };

    if (!savedConsent) {
        return defaultConsent;
    }

    try {
        return JSON.parse(savedConsent);
    } catch (e) {
        console.error("Error parsing consent data:", e);
        return defaultConsent;
    }
}

/**
 * Check if marketing consent is granted
 *
 * @returns {boolean} True if marketing consent is granted
 */
export function hasMarketingConsent() {
    const consent = getConsentLevel();
    return consent.ad_storage === "granted";
}
